import axios from 'axios';
import { AppConfig, JWT } from '../config';
import { COLLECTION_POINT_LOGIN, CONSENT_SUBMIT_TIMEOUT_MS, CONSENT_TEXT_ID } from '../config/consent';

// FEAT-052 (#493): 동의 적재 호출.
//
// **이 파일은 RAYConsent 를 직접 부르지 않는다.** 적재 토큰은 서버 전용이고, 프런트에 두면
// CRA 가 번들에 인라인해 공개된다(현재 iubenda 키가 그 상태 — #481). 우리 BE(`rayguard-api`)가
// 경유해 호출하고, 프런트는 **무엇에 동의했는지만** 보낸다.
//
// **subject_id 를 프런트가 보내지 않는다.** BE 가 토큰(JWT)에서 사용자를 꺼내 `_id` 로 파생한다.
// 프런트가 보내면 다른 사람 명의로 동의를 남길 수 있다.
//
// 교체 지점 — RAYConsent 적재 스키마나 엔드포인트가 바뀌어도 수정은 BE 와 이 파일뿐이다.
// 모달(ConsentModal)과 게이트(ConsentGate)는 적재가 어디로 가는지 모른다.

// 홈페이지 실측 조언: 적재 실패가 본래 기능을 막으면 안 된다. 이 함수는 **절대 throw 하지
// 않는다.** 그리고 감쌀 곳은 호출만이 아니라 **설정·헤더 준비 단계까지**다 — 그쪽은 설정
// 로드를 try/catch 밖에 둬서 시크릿 조회 실패가 메일 발송까지 죽인 적이 있다.
/**
 * 「한 번의 동의 행위」를 가리키는 식별자를 만든다. **모달이 열릴 때마다 한 번** 만들고,
 * 그 모달 안의 재시도는 같은 값을 쓴다.
 *
 * 🔴 이 값이 멱등 키의 일부가 된다. 양쪽으로 틀릴 수 있다 —
 *  - 매 전송마다 새로 만들면: 네트워크 재시도가 **중복 기록**을 만들어 같은 사람이 두 번
 *    동의한 것처럼 보인다
 *  - 아예 안 보내거나 고정값이면: 진짜 새 동의가 `duplicate` 로 먹혀 **기록이 안 쌓이는데
 *    성공으로 읽힌다.** 오류가 아니라 정상 응답(200)이라 검증이 거짓 통과한다
 *
 * crypto.randomUUID 는 secure context 에서만 있다. 폴백은 충돌만 피하면 되고 암호학적
 * 강도가 필요하지 않다 — 이 값은 비밀이 아니라 **구분자**다.
 */
export const newAttemptId = () => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    } catch (e) { /* 아래 폴백 */ }
    return `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * 동의를 적재한다.
 *
 * @param {object} p
 * @param {string} p.textVersion  이용자가 **실제로 본** 판 번호. 생략 불가 — RAYConsent 가
 *                                생략을 「최신」으로 해석하지 않고 거부한다.
 * @param {string} p.locale       이용자가 **실제로 본 문서의 로케일**(화면 언어가 아니다).
 * @param {boolean} p.granted     동의 여부. 거부도 기록한다 — 생략을 동의로 해석하지 않는다.
 * @param {string} p.attemptId    이 동의 행위의 식별자(newAttemptId). 멱등 키의 일부가 된다.
 * @returns {Promise<{ok: boolean, duplicate?: boolean, reason?: string}>}
 */
export const submitConsent = async ({ textVersion, locale, granted = true, attemptId }) => {
    try {
        if (!attemptId) {
            // 멱등 키를 만들 수 없다. BE 가 거부하므로 왕복하지 않고 여기서 끊는다.
            console.warn('[consentService] submit skipped — missing attemptId', {
                handler: 'consentService.submitConsent',
                reason: 'idempotency key cannot be built',
            });
            return { ok: false, reason: 'MISSING_ATTEMPT_ID' };
        }
        if (!textVersion || !locale) {
            // 판을 가리키지 못하는 기록은 남기지 않는다. 틀린 증거를 쌓느니 안 쌓는다.
            console.warn('[consentService] submit skipped — missing text version or locale', {
                handler: 'consentService.submitConsent',
                hasVersion: !!textVersion,
                hasLocale: !!locale,
                reason: 'record would not point at a specific policy version',
            });
            return { ok: false, reason: 'MISSING_TEXT_VERSION' };
        }

        const body = {
            type: 'record',
            data: {
                textId: CONSENT_TEXT_ID,
                textVersion: String(textVersion),
                locale: String(locale),
                granted: granted === true,
                attemptId: String(attemptId),
                // 기록의 collected_at 은 **BE 가 서버 시각으로** 정한다(클라이언트 시계는
                // 임의로 틀릴 수 있고 이건 증거로 남는 값이다). 이 값은 진단용이다.
                collectedAt: new Date().toISOString(),
                collectionPoint: COLLECTION_POINT_LOGIN,
            },
        };

        const res = await axios.post(
            `${AppConfig.API_SERVER_PATH}/consent`,
            body,
            { ...JWT(), timeout: CONSENT_SUBMIT_TIMEOUT_MS },
        );

        const data = res?.data;
        if (data?.status === 'success') {
            return { ok: true, duplicate: data?.data?.duplicate === true };
        }
        console.warn('[consentService] submit returned non-success', {
            handler: 'consentService.submitConsent',
            errmsg: data?.errmsg || 'unknown',
            reason: 'backend did not report success',
        });
        return { ok: false, reason: data?.errmsg || 'UNKNOWN' };
    } catch (err) {
        console.warn('[consentService] submit failed', {
            handler: 'consentService.submitConsent',
            reason: err?.message || 'unknown error',
        });
        return { ok: false, reason: 'REQUEST_FAILED' };
    }
};

export default submitConsent;
